import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { setReauthRequired } from '../auth/slices/authSlice';
import {
  clearCredentials,
  getAccessCredential,
} from '../auth/utils/inMemoryTokenStore';

import { oidcService } from './oidcService';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

export const axiosClient = axios.create();

axiosClient.interceptors.request.use((config) => {
  const cred = getAccessCredential();
  if (cred) {
    config.headers.Authorization = `Bearer ${cred.value}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retried
    ) {
      originalRequest._retried = true;

      try {
        await oidcService.refreshAccessToken('unauthorized_response');

        const newCred = getAccessCredential();
        if (newCred && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newCred.value}`;
        }

        return axiosClient(originalRequest);
      } catch {
        clearCredentials();
        // Lazy import to avoid circular dependency at module initialisation time
        const { default: store } = await import('../store');
        store.dispatch(
          setReauthRequired('Your session has expired. Please sign in again.')
        );
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
